import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Star, ArrowDown } from "lucide-react";

const HERO_IMG = "/images/storefront.jpg";

export default function Hero() {
  const { t } = useLanguage();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="La Hacienda Michoacana Supermarket storefront in Birmingham, AL"
          className="w-full h-full object-cover"
          style={{ objectPosition: "center 35%" }}
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#232321]/90 via-[#232321]/65 to-[#232321]/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#232321]/40 via-transparent to-[#232321]/90" />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl w-full px-6 md:px-10 pb-20 md:pb-28 pt-32">
        <div className="reveal max-w-4xl">
          <div className="inline-flex items-center gap-3 rounded-full border border-[#F9F6F0]/30 px-4 py-1.5 text-[#F9F6F0]/90 text-[12px] uppercase tracking-[0.2em] mb-8">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#E6A85C]" />
            {t("hero.eyebrow")}
          </div>

          <h1
            data-testid="hero-title"
            className="font-serif-display text-[#F9F6F0] tracking-tight leading-[0.95] text-[44px] sm:text-[68px] md:text-[92px] lg:text-[112px]"
          >
            <span className="block font-light italic text-[#F9F6F0]/85">{t("hero.titleA")}</span>
            <span className="block text-[#E6A85C]">{t("hero.titleB")}</span>
            <span className="block font-light text-[28px] sm:text-[36px] md:text-[44px] mt-3 max-w-2xl text-[#F9F6F0]/90">
              {t("hero.titleC")}
            </span>
          </h1>

          <p className="mt-8 max-w-xl text-[#F9F6F0]/85 text-base md:text-lg leading-relaxed">
            {t("hero.subtitle")}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-menu-cta"
              onClick={() => scrollTo("menu")}
              className="rounded-full bg-[#C05A3A] hover:bg-[#A54C30] text-[#F9F6F0] px-7 py-3.5 text-[14px] font-medium tracking-wide transition-all hover:-translate-y-0.5 shadow-lg shadow-[#C05A3A]/20"
            >
              {t("hero.ctaMenu")}
            </button>
            <button
              data-testid="hero-visit-cta"
              onClick={() => scrollTo("visit")}
              className="rounded-full border border-[#F9F6F0]/40 hover:border-[#F9F6F0] text-[#F9F6F0] px-7 py-3.5 text-[14px] font-medium tracking-wide transition-all backdrop-blur-sm hover:bg-[#F9F6F0]/10"
            >
              {t("hero.ctaVisit")}
            </button>
            <div className="flex items-center gap-2 text-[#F9F6F0]/85 text-[13px] ml-1">
              <div className="flex items-center gap-0.5 text-[#E6A85C]">
                {[...Array(5)].map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- static 5-star array, never reorders
                  <Star key={i} className="h-4 w-4 fill-current" strokeWidth={0} />
                ))}
              </div>
              <span>{t("hero.rating")}</span>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <button
          onClick={() => scrollTo("about")}
          className="mt-16 hidden md:flex items-center gap-3 text-[#F9F6F0]/70 hover:text-[#F9F6F0] text-[12px] uppercase tracking-[0.25em]"
          data-testid="hero-scroll-cue"
        >
          <ArrowDown className="h-4 w-4 animate-bounce" strokeWidth={1.5} />
          Scroll
        </button>
      </div>
    </section>
  );
}
